/**
 * AuditCisoCallout — "Need a regulator-grade PQC audit?" strip on the /audit
 * page (Iter270).
 *
 * Developer-tier visitors who land on /audit and realize mid-page they're
 * actually a CISO-tier buyer (e.g., they need a signed engagement, not a
 * $149 one-shot) get a high-contrast callout linking straight to the PQC
 * Audit Suite configurator at /pricing/audits#pqc-suite.
 *
 * Closes the conversion gap between the developer page and the
 * Enterprise menu.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, FileSignature } from "lucide-react";


export default function AuditCisoCallout() {
  return (
    <section className="max-w-[1200px] mx-auto px-6 sm:px-8 py-10"
             data-testid="audit-ciso-callout">
      <div className="qt-card relative overflow-hidden"
           style={{
             border: "1px solid rgba(31,221,131,0.42)",
             background:
               "radial-gradient(ellipse at top right, rgba(31,221,131,0.10), transparent 65%), " +
               "linear-gradient(135deg, rgba(0,229,255,0.05), transparent 65%)",
             padding: "1.75rem 1.75rem 1.5rem",
           }}>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="qt-mono text-xs"
                 style={{ color: "#1FDD83", letterSpacing: "0.20em", fontWeight: 700 }}
                 data-testid="audit-ciso-eyebrow">
              <Sparkles size={12} className="inline mr-1.5" />
              CISO / PROTOCOL LEAD?
            </div>
            <h3 className="qt-display text-2xl sm:text-3xl mt-2"
                style={{ color: "#fff", lineHeight: 1.15 }}
                data-testid="audit-ciso-headline">
              Need a{" "}
              <span style={{ color: "#1FDD83" }}>regulator-grade PQC audit</span>?
            </h3>
            <p className="mt-3 max-w-2xl text-sm sm:text-base"
               style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
              The self-serve audits on this page cover the SaaS path —
              great for active dev teams, $149/$99/$199 tiers, AI + senior
              reviewer. If you need a <b style={{ color: "#fff" }}>signed
              engagement letter</b>, a scoped cryptographic surface review
              (Hybrid Signature, EIP-7702, MPC Wallet), and a 2–4 week
              senior-cryptographer deliverable, that's the{" "}
              <b style={{ color: "#1FDD83" }}>PQC Audit Suite</b> —
              priced $12k–$45k per scope, bundle 2 of 3 for 15% off, all
              3 for 25%.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <Chip icon={ShieldCheck}    label="Signed certificate" />
              <Chip icon={FileSignature} label="Engagement letter · SOW" />
              <Chip icon={Sparkles}      label="Bundle: -25% on all 3" />
            </div>
          </div>

          <Link to="/pricing/enterprise#pqc-suite"
                data-testid="audit-ciso-cta"
                className="qt-btn qt-btn-primary"
                style={{
                  background: "#1FDD83",
                  color: "#04060a",
                  fontWeight: 700,
                  fontSize: 13,
                  padding: "0.85rem 1.4rem",
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                }}>
            Open the PQC Audit Suite <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}


function Chip({ icon: Icon, label }) {
  return (
    <span className="qt-mono text-[10px] inline-flex items-center gap-1.5"
          style={{
            color: "var(--qt-text-2)",
            letterSpacing: "0.10em",
            padding: "5px 10px",
            borderRadius: 99,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid var(--qt-border)",
          }}>
      <Icon size={11} style={{ color: "#1FDD83" }} />
      {label.toUpperCase()}
    </span>
  );
}
